// WorkspaceContext — the lens (Epic 2 · 00_CONTRACT.md §10).
//
// A workspace is WHO IS LOOKING; a product is WHOSE DATA IT IS. The provider
// calls fn_my_workspaces() ONCE per session and holds the result. One entry
// means no switcher (the product simply is their workspace); >1 shows it.
//
// Capability resolution is also once-per-session: the provider pre-fetches
// every capability the person holds via fn_has_capability for each key in
// CAPABILITY_KEYS, so can() is a lookup, not a query per render.
//
// The wall is the database — every function checks auth.uid() itself. The
// menu is a convenience, not the wall. A URL for a workspace someone doesn't
// hold returns nothing regardless of what the menu shows.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyWorkspaces, hasCapability } from "../api/hub";

// The full capability registry (00_CONTRACT.md §10). Resolved once per session
// so `can()` is a lookup, never a per-render RPC.
//
// `admin.manage` — held by Brian and Aly only. Gates the Team & Admin write
// screens (reporting line, signals, pipelines, services, SLA). This is the
// intended design, not a temporary state — per the Team & Admin backend answers
// (2026-09-14), permissions come from the database, never a role name.
export const CAPABILITY_KEYS = [
  "work.read",
  "work.write",
  "thread.read",
  "thread.write",
  "effort.read.self",
  "effort.read.all",
  "billing.read",
  "dm.read",
  "agent.approve",
  "agent.manage",
  "admin.manage",
  "clients.all",
];

// Derive the workspace key from the URL prefix. The prefix IS the workspace
// (Epic 2 · S2.3): `/hub/...` → team, `/p/:clientId/...` → park, `/o/:orgId/...`
// → operator, `/r/...` → research. The prefix is permanent — a page slug never
// changes when it appears in a second workspace.
const WORKSPACE_PREFIXES = ["hub", "p", "o", "r"];
const workspaceFromPath = (pathname) => {
  const seg = (pathname || "").split("/").filter(Boolean)[0];
  return WORKSPACE_PREFIXES.includes(seg) ? seg : null;
};

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capabilities, setCapabilities] = useState({});

  // The active workspace is derived from the URL prefix — not stored in state.
  // The prefix IS the workspace (Epic 2 · S2.3).
  const routeWorkspace = workspaceFromPath(location.pathname);

  // Fetch workspaces once per session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getMyWorkspaces();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (cancelled) return;
        setWorkspaces(list);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Couldn't load workspaces.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve capabilities once per session. Each key is a separate fn_has_capability
  // call; they run in parallel and settle into the capabilities map so can() is a
  // lookup, not a query per render.
  //
  // 2026-10-12 — capabilities are a USER-level signal (fn_has_capability resolves
  // via auth.uid()), independent of whether the caller holds a workspace row. The
  // earlier gate (`if (loading || workspaces.length === 0) return`) coupled them
  // to the workspace fetch: if fn_my_workspaces returned empty (or failed
  // transiently → workspaces=[]), capabilities NEVER resolved, so can("admin.manage")
  // stayed false for the whole session and the Client Hub → Admin section was
  // hidden "sometimes". That is the same class of bug AdminHeader already fixed for
  // the dashboard toggle (it dropped its workspaces.length>0 gate for exactly this
  // reason). Decoupling here means an admin who holds no workspace row, or whose
  // workspace fetch blipped, still sees the Admin section. The DB remains the wall —
  // every hub function checks auth.uid() itself; the menu is a convenience.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        CAPABILITY_KEYS.map(async (key) => {
          const res = await hasCapability(key);
          return [key, res?.data === true];
        }),
      );
      if (cancelled) return;
      const map = {};
      for (const r of results) {
        if (r.status === "fulfilled") {
          const [key, held] = r.value;
          map[key] = held;
        }
      }
      if (!cancelled) setCapabilities(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The active workspace object — matched from the URL prefix against the
  // fetched list. If the route doesn't match a workspace prefix (e.g. /dashboard),
  // falls back to the first workspace.
  const activeWsObj = useMemo(() => {
    if (workspaces.length === 0) return null;
    if (routeWorkspace) {
      const match = workspaces.find(
        (w) => (w.workspace || w.id) === routeWorkspace,
      );
      if (match) return match;
    }
    return workspaces[0] || null;
  }, [workspaces, routeWorkspace]);

  const activeWorkspaceId = activeWsObj?.workspace || activeWsObj?.id || null;
  const scopeType = activeWsObj?.scope_type || null;
  const hasMultiple = workspaces.length > 1;

  // Switch workspace — navigates to the target workspace's default_route.
  // The URL prefix changes, and the provider picks up the new active workspace
  // from the route on the next render.
  const switchWorkspace = useCallback(
    (targetId) => {
      const ws = workspaces.find((w) => (w.workspace || w.id) === targetId);
      const route = ws?.default_route || `/${targetId}`;
      navigate(route);
    },
    [workspaces, navigate],
  );

  // can(key) — a lookup, not a query per render.
  const can = useCallback((key) => !!capabilities[key], [capabilities]);

  const value = useMemo(
    () => ({
      workspaces,
      activeWorkspace: activeWsObj,
      activeWorkspaceId,
      switchWorkspace,
      scopeType,
      hasMultiple,
      loading,
      error,
      can,
      capabilities,
    }),
    [
      workspaces,
      activeWsObj,
      activeWorkspaceId,
      switchWorkspace,
      scopeType,
      hasMultiple,
      loading,
      error,
      can,
      capabilities,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}

// Convenience hook — just the `can` function.
export function useCan() {
  return useWorkspace().can;
}
